import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatDuration, formatPlays, formatDate, getCategoryName } from "@/utils/video";
import { useFavorites } from "@/hooks/useFavorites";
import { showToast } from "@/components/feature/Toast";

interface VideoCardProps {
  video: Video;
  showDate?: boolean;
  showCategory?: boolean;
}

export default function VideoCard({ video, showDate = false, showCategory = false }: VideoCardProps) {
  const { toggleFavorite, isFavorite } = useFavorites();
  const fav = isFavorite(video.id);

  const onToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = toggleFavorite(video.id);
    showToast(next ? "已加入收藏" : "已取消收藏");
  };

  return (
    <Link to={`/detail/${video.id}`} className="group block">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-secondary-100">
        <img
          src={video.cover}
          alt={video.title}
          title={`${video.title} 糖豆视频`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <span className="absolute bottom-2 right-2 px-1.5 py-0.5 text-xs font-label text-background-50 bg-foreground-950/70 rounded">
          {formatDuration(video.duration)}
        </span>
        {showCategory && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs text-background-50 bg-primary-500/90 rounded-full">
            {getCategoryName(video.category)}
          </span>
        )}
        <button
          type="button"
          aria-label={fav ? "取消收藏" : "收藏"}
          onClick={onToggle}
          className={`absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
            fav ? "bg-accent-500 text-background-50" : "bg-foreground-950/45 text-background-50 hover:bg-foreground-950/70"
          }`}
        >
          <i className={fav ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
        </button>
      </div>
      <h3 className="mt-3 text-sm md:text-base font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
        {video.title}
      </h3>
      <div className="mt-1.5 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-foreground-500">
        <span className="flex items-center gap-1">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-play-circle-line"></i>
          </span>
          {formatPlays(video.plays)} 次播放
        </span>
        {showDate && <span>{formatDate(video.publishDate)}</span>}
      </div>
    </Link>
  );
}